import React from 'react'
import { DprSaveStatus } from '../core/useDprAutoSave'
import { DprSectionLease } from '../core/useDprRealtime'
import { KTSVG } from '../../../../_metronic/helpers'

interface Props {
  saveStatus: DprSaveStatus
  lastSavedTime: Date | null
  lastError: string | null
  isConnected: boolean
  leases: DprSectionLease[]
  onOpenHistory: () => void
}

export const DprRealtimeStatusBar: React.FC<Props> = ({
  saveStatus,
  lastSavedTime,
  lastError,
  isConnected,
  leases,
  onOpenHistory,
}) => {
  const otherUsersLeases = leases.filter((l) => !l.isCurrentUsersLease)

  const formatTime = (d: Date) => {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
  }

  return (
    <div className='d-flex flex-wrap align-items-center justify-content-between p-3 mb-4 rounded bg-light-primary border border-primary border-opacity-25'>
      {/* ── Left: Auto-Save Status ── */}
      <div className='d-flex align-items-center gap-3'>
        <div className='d-flex align-items-center gap-2'>
          {saveStatus === 'SAVING' && (
            <>
              <div className='spinner-border spinner-border-sm text-primary' role='status' />
              <span className='fw-bold text-primary fs-7'>Saving changes...</span>
            </>
          )}

          {saveStatus === 'SAVED' && (
            <>
              <span className='badge badge-light-success d-flex align-items-center gap-1 fs-7 py-2 px-3'>
                <i className='bi bi-check-circle-fill text-success fs-6'></i>
                <span className='fw-bold'>Auto-saved</span>
                {lastSavedTime && (
                  <span className='text-muted fs-8 ms-1'>at {formatTime(lastSavedTime)}</span>
                )}
              </span>
            </>
          )}

          {saveStatus === 'LOCKED' && (
            <span className='badge badge-light-danger d-flex align-items-center gap-1 fs-7 py-2 px-3'>
              <i className='bi bi-lock-fill text-danger fs-6'></i>
              <span className='fw-bold'>{lastError || 'Section locked by another user'}</span>
            </span>
          )}

          {saveStatus === 'ERROR' && (
            <span className='badge badge-light-danger d-flex align-items-center gap-1 fs-7 py-2 px-3'>
              <i className='bi bi-exclamation-triangle-fill text-danger fs-6'></i>
              <span className='fw-bold'>{lastError || 'Save failed'}</span>
            </span>
          )}

          {saveStatus === 'IDLE' && (
            <span className='badge badge-light-secondary fs-8'>
              <i className='bi bi-cloud-check text-muted me-1'></i>
              Auto-save active
            </span>
          )}
        </div>

        {/* Live SSE indicator */}
        <div className='d-flex align-items-center gap-1 fs-8 text-muted'>
          <span
            className={`bullet bullet-dot ${
              isConnected ? 'bg-success' : 'bg-warning'
            } h-8px w-8px`}
          ></span>
          <span>{isConnected ? 'Real-time sync on' : 'Connecting...'}</span>
        </div>
      </div>

      {/* ── Center / Right: Active Collaborators & Audit History Button ── */}
      <div className='d-flex align-items-center gap-3 mt-2 mt-sm-0'>
        {otherUsersLeases.length > 0 && (
          <div className='d-flex align-items-center gap-2'>
            <span className='fs-8 fw-semibold text-muted'>Editing now:</span>
            {otherUsersLeases.map((lease) => (
              <span
                key={`${lease.sectionName}-${lease.userId}`}
                className='badge badge-light-warning d-flex align-items-center gap-1 fs-8 py-1 px-2'
                title={`Locked by ${lease.userName} (${lease.userRank || 'Crew'}) until ${new Date(
                  lease.expiresAt
                ).toLocaleTimeString()}`}
              >
                <i className='bi bi-person-fill text-warning'></i>
                <span className='fw-bold'>{lease.userName}</span>
                <span className='badge badge-secondary py-0 px-1 text-uppercase fs-9'>
                  {lease.sectionName}
                </span>
              </span>
            ))}
          </div>
        )}

        {/* Audit History Drawer Button */}
        <button
          type='button'
          onClick={onOpenHistory}
          className='btn btn-sm btn-light-primary d-flex align-items-center gap-1 py-1 px-3'
        >
          <i className='bi bi-clock-history fs-6'></i>
          <span className='fw-bold fs-7'>Change History</span>
        </button>
      </div>
    </div>
  )
}
